import { useState } from 'react';
import { Loader2, BarChart3, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function Kv({ label, value, mono = false }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-muted-foreground min-w-[160px] shrink-0">{label}:</span>
      <span className={mono ? 'font-mono' : ''}>{value ?? 'n/a'}</span>
    </div>
  );
}

const BUCKET_LABELS = {
  clean: 'Clean',
  vat_basis_only: 'VAT Basis Only',
  currency_mismatch: 'Currency Mismatch',
  consolidated_multi_invoice: 'Consolidated (Multi-Invoice)',
  real_residual: 'Real Residual',
};

const BUCKET_ORDER = [
  'clean',
  'vat_basis_only',
  'currency_mismatch',
  'consolidated_multi_invoice',
  'real_residual',
];

function BucketSection({ name, data }) {
  const [open, setOpen] = useState(false);
  if (!data) return null;

  return (
    <div className="border rounded-lg p-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full text-left"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <span className="text-sm font-medium">{BUCKET_LABELS[name] ?? name}</span>
        <span className="text-sm font-mono text-muted-foreground ml-auto">
          count: {data.count} | residual: {data.total_residual}
        </span>
      </button>
      {open && data.samples && data.samples.length > 0 && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-1.5">Booking</th>
                <th className="text-left p-1.5">Currency</th>
                <th className="text-left p-1.5">Inv Currencies</th>
                <th className="text-right p-1.5">Expected (ex-VAT)</th>
                <th className="text-right p-1.5">Invoiced (ex-VAT)</th>
                <th className="text-right p-1.5">Invoiced (incl)</th>
                <th className="text-right p-1.5">Invoices</th>
                <th className="text-left p-1.5">Invoice IDs</th>
              </tr>
            </thead>
            <tbody>
              {data.samples.map((s, i) => (
                <tr key={i} className="border-t">
                  <td className="p-1.5 font-mono">{s.booking_id}</td>
                  <td className="p-1.5">{s.currency}</td>
                  <td className="p-1.5 font-mono text-[10px]">{(s.currencies ?? []).join(', ')}</td>
                  <td className="p-1.5 text-right font-mono">{s.expected_ex_vat}</td>
                  <td className="p-1.5 text-right font-mono">{s.invoiced_ex_vat}</td>
                  <td className="p-1.5 text-right font-mono">{s.invoiced_incl}</td>
                  <td className="p-1.5 text-right font-mono">{s.invoice_count}</td>
                  <td className="p-1.5 font-mono text-[10px]">{(s.invoice_ids ?? []).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function VatGapPopulationPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.reconClassifier();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const r = result;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-orange-600" />
            Reconciliation Classifier
          </CardTitle>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <BarChart3 className="h-4 w-4 mr-2" />
            )}
            Scan
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Portfolio-wide booking-vs-invoice reconciliation classifier.
          Joins on invoice booking_id (exact), compares ex-VAT subtotals,
          FX-converts per invoice. Read-only, no writes.
        </p>
      </CardHeader>
      {r && (
        <CardContent className="space-y-3">
          {/* ── Three exposure populations ── */}
          <div className="border rounded-lg p-3 space-y-1 bg-amber-50/50">
            <p className="text-xs font-medium text-amber-800 mb-1">Exposure populations</p>
            <Kv
              label="Uninvoiced roots (root-grain)"
              value={`${r.zero_invoice_bookings?.count ?? 0} (total expected ex-VAT: ${r.zero_invoice_bookings?.total_expected_ex_vat ?? '0'})`}
              mono
            />
            <Kv
              label="Under-invoiced (gross)"
              value={`${r.matched_exposure?.under_invoiced_count ?? 0} (shortfall: ${r.matched_exposure?.under_invoiced_total ?? '0'})`}
              mono
            />
            <Kv
              label="Over-invoiced (gross)"
              value={`${r.matched_exposure?.over_invoiced_count ?? 0} (overage: ${r.matched_exposure?.over_invoiced_total ?? '0'})`}
              mono
            />
            <Kv
              label="  deposits found"
              value={`${r.matched_exposure?.deposit_count ?? 0} (billed: ${r.matched_exposure?.deposit_billed_count ?? 0}, total: ${r.matched_exposure?.deposit_total ?? '0'})`}
              mono
            />
            {r.matched_exposure?.deposit_status_distribution && Object.keys(r.matched_exposure.deposit_status_distribution).length > 0 && (
              <Kv
                label="    deposit status dist."
                value={Object.entries(r.matched_exposure.deposit_status_distribution).map(([k, v]) => `${k}: ${v}`).join(', ')}
                mono
              />
            )}
            <Kv
              label="  genuine over"
              value={`${r.matched_exposure?.genuine_over_count ?? 0} (total: ${r.matched_exposure?.genuine_over_total ?? '0'})`}
              mono
            />
            <Kv
              label="Net residual (net, may hide exposure)"
              value={r.matched_exposure?.net_residual ?? '0'}
              mono
            />
            <Kv
              label="Orphan booking IDs"
              value={`${r.unmatched_invoices?.orphan_booking_id?.count ?? 0} (GBP: ${r.unmatched_invoices?.orphan_booking_id?.total_gbp ?? '0'})`}
              mono
            />
            <Kv
              label="Ad-hoc invoices (no booking)"
              value={`${r.unmatched_invoices?.ad_hoc?.count ?? 0} (GBP: ${r.unmatched_invoices?.ad_hoc?.total_gbp ?? '0'})`}
              mono
            />
          </div>

          {/* ── Bucket summary ── */}
          <div className="border rounded-lg p-3 space-y-1">
            {BUCKET_ORDER.map((name) => (
              <Kv
                key={name}
                label={BUCKET_LABELS[name]}
                value={r.buckets?.[name]?.count ?? 0}
                mono
              />
            ))}
          </div>

          {BUCKET_ORDER.map((name) => (
            <BucketSection key={name} name={name} data={r.buckets?.[name]} />
          ))}

          {r.generated_at && (
            <p className="text-[10px] text-muted-foreground text-right">
              Generated {r.generated_at}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
